import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  LinearProgress,
  Stack
} from "@mui/material";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import { Technology, TechnologyItem } from "../../Graphql/types/technology";

interface Props {
  open: boolean;
  onClose: () => void;
  technologyItemIndex: number;
  technology: Technology;
  onSave: (item: TechnologyItem, index: number) => void;
  isUpdating?: boolean;
}

const urlRegex =
  /^(https?:\/\/)?([\w.-]+)\.([a-zA-Z]{2,})(:\d+)?(\/[\w.-]*)*(\?[^\s]*)?(#[^\s]*)?$/i;

// Validation schema
const validationSchema: Yup.ObjectSchema<TechnologyItem> = Yup.object().shape({
  title: Yup.string()
    .required("Title is required")
    .min(3, "Title must be at least 3 characters")
    .matches(/^[A-Za-z\s'-.]+$/, "Special chars are not allowed"),
  description: Yup.string()
    .required("Description is required")
    .min(10, "Description must be at least 10 characters")
    .matches(/^[A-Za-z\s'-.()]+$/, "Special chars are not allowed"),
  detailUrl: Yup.string()
    .required("Detail url is required")
    .matches(urlRegex, "Please use a valid url.")
});

const EditTechnologyItemDialog: React.FC<Props> = (props) => {
  const { open, onClose, technologyItemIndex, onSave, isUpdating, technology } =
    props;
  const technologyItem = technology.technologyItems[technologyItemIndex] || {};
  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm<TechnologyItem>({
    resolver: yupResolver(validationSchema),
    defaultValues: technologyItem
  });

  // save the technology
  const handleUpdateTechItem = (item: TechnologyItem): void => {
    onSave(item, technologyItemIndex);
  };

  const isEmpty = Object.keys(technologyItem).length === 0;

  return (
    <Dialog open={open} onClose={onClose} fullWidth>
      <DialogTitle textAlign="center">{`${isEmpty ? "Create" : "Edit"} Technology Item`}</DialogTitle>
      <DialogContent>
        <TextField
          fullWidth
          margin="dense"
          label="Title"
          {...register("title")}
          error={!!errors.title}
          helperText={errors.title?.message}
        />
        <TextField
          fullWidth
          margin="dense"
          label="Description"
          multiline
          minRows={3}
          {...register("description")}
          error={!!errors.description}
          helperText={errors.description?.message}
        />
        <TextField
          fullWidth
          margin="dense"
          label="Detail URL"
          {...register("detailUrl")}
          error={!!errors.detailUrl}
          helperText={errors.detailUrl?.message}
        />
      </DialogContent>
      {isUpdating && (
        <Stack width="100%" paddingBottom="1em" alignSelf="center">
          <LinearProgress />
        </Stack>
      )}
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSubmit(handleUpdateTechItem)} color="primary">
          {`${isEmpty ? "Create" : "Save"}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditTechnologyItemDialog;
